import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
  icon?: ReactNode;
  /** Information-only dialog: a single button, which resolves `true`. */
  hideCancel?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export const useConfirm = (): ConfirmFn => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within <ConfirmDialogProvider>");
  }
  return ctx;
};

const ConfirmMedia = ({
  variant = "default",
  icon,
}: {
  variant?: ConfirmOptions["variant"];
  icon?: ReactNode;
}) => {
  const tone =
    variant === "destructive"
      ? "bg-destructive/10 text-destructive"
      : "bg-primary-light text-primary";
  return (
    <div
      className={cn(
        "mx-auto flex h-12 w-12 items-center justify-center rounded-lg",
        tone
      )}
    >
      {icon ?? <AlertTriangle className="h-6 w-6" />}
    </div>
  );
};

export const ConfirmDialogProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const resolve = (value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpen(false);
  };

  // Radix bug workaround: when a Dialog opens directly from a Radix
  // DropdownMenu/Popover item, the parent overlay can leave `pointer-events:
  // none` on <body> after closing. Reset it after the close transition.
  useEffect(() => {
    if (open) return;
    const t = window.setTimeout(() => {
      if (document.body.style.pointerEvents === "none") {
        document.body.style.pointerEvents = "";
      }
    }, 200);
    return () => window.clearTimeout(t);
  }, [open]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) resolve(false);
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <div className="flex flex-col items-center text-center space-y-3">
            <ConfirmMedia variant={options?.variant} icon={options?.icon} />
            <DialogTitle className="text-base">{options?.title}</DialogTitle>
            <DialogDescription
              className={cn(
                "text-sm text-muted-foreground",
                !options?.description && "sr-only"
              )}
            >
              {options?.description ?? "Confirmation"}
            </DialogDescription>
          </div>
          <div
            className={cn(
              "grid gap-2 pt-2",
              options?.hideCancel ? "grid-cols-1" : "grid-cols-2"
            )}
          >
            {!options?.hideCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={() => resolve(false)}
                className="h-auto min-h-10 w-full whitespace-normal py-2 leading-tight"
              >
                {options?.cancelText ?? "Annuler"}
              </Button>
            )}
            <Button
              type="button"
              variant={options?.variant ?? "default"}
              onClick={() => resolve(true)}
              className="h-auto min-h-10 w-full whitespace-normal py-2 leading-tight"
            >
              {options?.confirmText ?? "Confirmer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
};
